import { datadogRum } from '@datadog/browser-rum';
import { maskJsonSensitiveData } from 'utils/maskJsonSensitiveData';
import type { NormalizedError, DefaultErrorJson } from './types';

const { VITE_STAGE } = import.meta.env;

export type DefaultErrorParams = {
  name?: string;
  message?: string;
  title?: string;
  error?: Error;
  dd_fingerprint?: string;
  context?: Record<string, unknown>;
};
export class DefaultError<
  T extends DefaultErrorJson = DefaultErrorJson
> extends Error {
  title: string;
  keyword: string;
  dd_fingerprint?: string;
  context?: Record<string, unknown>;
  response?: {
    errors?: NormalizedError[];
    [key: string]: unknown;
  };
  cause?: Error;
  name: string;

  constructor({
    error,
    name,
    message,
    title,
    dd_fingerprint,
    context
  }: DefaultErrorParams) {
    super(error?.message);

    /* istanbul ignore next -- @preserve */
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }

    this.name = name ?? DefaultError.name;
    this.title = title ?? 'Error';
    this.message = message ?? error?.message ?? 'Unexpected error';
    this.keyword = '';
    this.dd_fingerprint = dd_fingerprint;
    this.context = context;
    this.cause = error as Error;

    this.#logError();
  }

  static get name() {
    return 'DefaultError';
  }

  #logError() {
    console.error(
      `${this.stack} \n\n:: caused by :: \n\n ${this.cause?.stack}\n\n:: context ::\n\n`,
      this.context
    );
    const eventData = maskJsonSensitiveData({
      stack: this.stack,
      name: this.name,
      ...this.context
    }) as Record<string, unknown>;
    /* istanbul ignore next -- @preserve */
    if (VITE_STAGE !== 'test') {
      datadogRum.addError(this, eventData);
      this.sendAdobeEvent(eventData);
    }
  }

  /* istanbul ignore next -- @preserve */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected sendAdobeEvent(_eventData: Record<string, unknown>): void {
    // override in subclasses to send Adobe analytics
  }

  toJson(): T {
    const EXCLUDED = new Set(['stack', 'cause', 'response']);
    return JSON.parse(
      JSON.stringify(
        this,
        Object.getOwnPropertyNames(this).filter(
          (k) => !EXCLUDED.has(k) && !k.startsWith('_')
        )
      )
    ) as T;
  }
}
